<template>
    <div>
        <h4 class="mb-4 _500">Knowledge Base</h4>

        <p>
            <small>
                <span v-if="isVoiceBot">
                    Enhance your agent's knowledge base with files, text, or webpages. These sources help your agent provide more accurate, 
                    contextual responses by learning from your specific content. This improves response quality and consistency over time.
                </span>
                <span v-else>
                    Upload documents to enhance your agent's knowledge base. These files help your agent provide more accurate, 
                    contextual responses by learning from your specific content. This improves response quality and consistency over time.
                </span>
            </small>
        </p>

        <div class="mb-0" v-loading="loading">
            <div v-if="bot.id" class="mb-3">
                <el-button size="small" @click="showAddFileModal = true" class="mr-2">
                    <i class="fa fa-file"></i> Add Files
                </el-button>
                <el-button 
                    v-if="isVoiceBot"
                    size="small"
                    @click="showAddTextModal = true" 
                    class="mr-2"
                >
                    <i class="fa fa-file-text"></i> Add Free Text
                </el-button>
                <el-button 
                    v-if="isVoiceBot"
                    size="small"
                    @click="showImportWebpageModal = true"
                >
                    <i class="fa fa-globe"></i> Import Webpage
                </el-button>
            </div>

            <p
                class="text-md _400 text-danger"
                v-if="bot.model === AloAi.MODEL_GPT_35_T"
            >
                You can't have KB files with this model.
            </p>

            <el-alert
                v-if="!bot.id"
                type="info"
                :closable="false"
                show-icon
            >
                <template slot="default">
                    <p class="mb-0">Create your AloAi Agent first to start building your knowledge base.</p>
                </template>
            </el-alert>

            <el-table
                class="w-full"
                row-key="id"
                :data="knowledge_base_files"
                v-if="bot.id && knowledge_base_files"
                stripe
            >
                <el-table-column label="Type" width="100">
                    <template v-slot="scope">
                        <span v-if="scope.row.type === 'file'" class="label type-badge type-badge-file">File</span>
                        <span v-else-if="scope.row.type === 'text'" class="label type-badge type-badge-text">Text</span>
                        <span v-else-if="scope.row.type === 'webpage'" class="label type-badge type-badge-webpage">Webpage</span>
                    </template>
                </el-table-column>
                <el-table-column label="Name">
                    <template v-slot="scope">
                        <span>{{ scope.row.name }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="Created At">
                    <template v-slot="scope">
                        <span>{{ scope.row.created_at | fixDateTime }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="Created by">
                    <template v-slot="scope">
                        <span>{{ scope.row.created_by?.name + ' - #' + scope.row.created_by?.id }}</span>
                    </template>
                </el-table-column>
                <el-table-column
                    width="100"
                    label="Status"
                    align="center"
                >
                    <template v-slot="scope">
                        <span v-if="scope.row.status === 'PROCESSING'" class="ml-2 d-inline-block label yellow">Processing</span>
                        <span v-if="scope.row.status === 'SUCCESS'" class="ml-2 d-inline-block label green">Success</span>
                        <span v-if="scope.row.status === 'FAILED'" class="ml-2 d-inline-block label red">Error</span>
                    </template>
                </el-table-column>
                <el-table-column
                    label="Operations"
                    align="center"
                    width="180"
                    fixed="right"
                >
                    <template v-slot="scope">
                        <div class="col-12" v-if="scope.row.type === 'text'">
                            <button
                                type="button"
                                class="btn btn-sm btn-block info"
                                :disabled="isContentProcessing(scope.row)"
                                @click.prevent="handleKnowledgeBaseContentEdit(scope.row)"
                            >
                                <i class="fa fa-pencil pull-left"></i>
                                Edit
                            </button>
                        </div>
                        <div class="col-12" :class="scope.row.type === 'text' ? 'mt-1' : ''">
                            <button
                                type="button"
                                class="btn btn-sm btn-block danger"
                                :disabled="isContentProcessing(scope.row)"
                                @click.prevent="handleKnowledgeBaseContentDelete(scope.row)"
                            >
                                <i class="fa fa-trash pull-left"></i>
                                Delete
                            </button>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- Modals -->
        <add-file-modal
            :visible.sync="showAddFileModal"
            :bot="bot"
            @close="showAddFileModal = false"
            @success="handleModalSuccess"
            @failed="handleModalFailed"
            @before-upload="loading = true"
        />

        <add-text-modal
            :visible.sync="showAddTextModal"
            :bot="bot"
            :content="editingContent"
            :is-editing="isEditingText"
            @close="handleAddTextModalClose"
            @success="handleModalSuccess"
            @failed="handleModalFailed"
        />

        <import-webpage-modal
            :visible.sync="showImportWebpageModal"
            :bot="bot"
            @close="showImportWebpageModal = false"
            @success="handleModalSuccess"
            @failed="handleModalFailed"
        />
    </div>
</template>

<script>
import { MessageBox } from 'element-ui';
import * as AloAi from '../../../constants/aloai';
import AddFileModal from './AddFileModal.vue';
import AddTextModal from './AddTextModal.vue';
import ImportWebpageModal from './ImportWebpageModal.vue';

export default {
    name: 'knowledge-base-section',

    components: {
        AddFileModal,
        AddTextModal,
        ImportWebpageModal
    },

    props: {
        bot: {
            required: true,
            type: Object
        }
    },

    data() {
        return {
            loading: false,
            knowledge_base_files: this.bot.knowledge_base_files,
            AloAi,
            showAddFileModal: false,
            showAddTextModal: false,
            showImportWebpageModal: false,
            editingContent: null,
            isEditingText: false
        }
    },

    computed: {
        isTextBot() {
            return this.bot.type === AloAi.TYPE_TEXT
        },

        isVoiceBot() {
            return this.bot.type === AloAi.TYPE_VOICE
        }
    },

    methods: {
        updateBot() {
            if (!this.bot.id) {
                return
            }

            this.loading = true
            axios.get('/api/v1/aloai/bot/' + this.bot.id)
                .then((res) => {
                    this.knowledge_base_files = res.data.knowledge_base_files
                })
                .catch((err) => {
                    console.error(err)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        
        isContentProcessing(row) {
            return row.status === 'PROCESSING'
        },

        handleKnowledgeBaseContentEdit(row) {
            if (row.type !== 'text') {
                return
            }

            this.editingContent = row
            this.isEditingText = true
            this.showAddTextModal = true
        },

        handleKnowledgeBaseContentDelete(row) {
            MessageBox.confirm('Are you sure you want to delete this content?', 'Warning', {
                confirmButtonText: 'Yes',
                cancelButtonText: 'Cancel',
                type: 'warning'
            }).then(async () => {
                this.loading = true

                axios.delete('/api/v1/aloai/bot/' + this.bot.id + '/delete-content', {
                    data: {
                        content_id: row.id,
                    }
                }).then((res) => {
                    this.updateBot()
                    this.$notify({
                        offset: 95,
                        title: 'Content Deleted',
                        message: res.data.message,
                        type: 'success',
                        showClose: true,
                    })
                }).finally(() => {
                    this.loading = false
                })
            }).catch(() => {
                // do nothing
            })
        },

        handleAddTextModalClose() {
            this.showAddTextModal = false
            this.editingContent = null
            this.isEditingText = false
        },

        handleModalSuccess() {
            this.loading = false
            this.editingContent = null
            this.isEditingText = false
            this.updateBot()
        },

        handleModalFailed() {
            this.loading = false
            this.editingContent = null
            this.isEditingText = false
        }
    },

    watch: {
        bot: {
            handler(newVal) {
                if (newVal.knowledge_base_files && newVal.knowledge_base_files.length) {
                    this.knowledge_base_files = newVal.knowledge_base_files
                }
            },
            deep: true,
            immediate: true
        }
    },

    mounted() {
        VueEvent.listen('aloai_bot_agent_knowledge_base_updated', (data) => {
            this.$notify({
                offset: 95,
                title: 'Knowledge Base on bot ' + data.bot,
                message: 'Knowledge base has been updated',
                type: 'success',
                showClose: true,
            })

            this.updateBot()
        })
    }
}
</script>

<style scoped>
.type-badge {
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 500;
    display: inline-block;
    color: white;
}

.type-badge-file {
    background-color: #909399;
}

.type-badge-text {
    background-color: #67c23a;
}

.type-badge-webpage {
    background-color: #e6a23c;
}
</style>
